import type { LocationQuery } from 'vue-router';

import type { GlobalClusterDefinition } from '~/utils/clusters';
import type { PreferredRegion } from '~/utils/geo';

import { isGeoMirroredPath } from '~/constants/routes/unified';
import {
  buildRegionSwitchUrl,
  hasNonTransferableQueryParams,
  omitNonTransferableQueryParams,
} from '~/utils/clusters';

export interface GeoPreferenceTargetInput {
  /** The stored region preference (already narrowed), or null when unset. */
  preference: PreferredRegion | null;
  /** Session decline of a region prompt — suppresses auto-routing. */
  dismissed: boolean;
  /**
   * Whether the visitor is signed in. Authenticated users belong to one cluster
   * (their account lives there); moving them to the sibling cluster would
   * silently log them out, so auto-routing is anonymous-only.
   */
  isAuthenticated: boolean;
  /** The teacher portal has its own cross-cluster guard and no region UX. */
  isSchools: boolean;
  /** Crawler user-agent — never prompted or routed (SEO). */
  isBot: boolean;
  /** Current cluster code from `X-Cluster-Code`; null on the root cluster. */
  clusterCode: string | null;
  /** Same-product cluster list (`useGlobalClusters().clusters`). */
  clusters: GlobalClusterDefinition[];
  /** The served cluster's geo (`useGlobalClusters().currentCluster.geo`). */
  currentGeo: string | null;
  path: string;
  query: LocationQuery;
}

export interface GeoPreferenceRedirectInput extends GeoPreferenceTargetInput {
  scheme: 'http' | 'https';
}

export interface GeoPreferenceTarget {
  cluster: GlobalClusterDefinition;
  /**
   * Whether the current path exists in every cluster. Mirrored → the SSR
   * middleware redirects straight to the equivalent; unmirrored → the client
   * offers the destination homepage via the confirmation dialog.
   */
  mirrored: boolean;
}

/**
 * The stand-down rules shared by EVERY geo prompt or route: never for the
 * teacher portal, signed-in visitors, crawlers, a session that declined a
 * prompt, an unlisted cluster (test/staging codes can't vouch for their
 * siblings — switching would jump to the real apex, same guard as hreflang),
 * or mid auth flow (login callbacks carry params a switch would strip,
 * silently breaking the sign-in).
 *
 * Composed by `resolveGeoPreferenceTarget` (returning visitors) and
 * `useGeoRouting.shouldAutoShowSelector` (first visits) so a new stand-down
 * is written once and holds everywhere.
 */
export const isGeoPromptEligible = (input: Omit<GeoPreferenceTargetInput, 'preference' | 'currentGeo'>): boolean =>
  !input.isSchools
  && !input.isAuthenticated
  && !input.isBot
  && !input.dismissed
  && !!input.clusterCode
  && input.clusters.some(cluster => cluster.code === input.clusterCode)
  && !hasNonTransferableQueryParams(input.query);

/**
 * THE eligibility policy for preference-based auto-routing, shared by both of
 * its surfaces so they can never disagree: the SSR redirect
 * (`resolveGeoPreferenceRedirect` below, via
 * `middleware/09.geo-preference-routing.global.ts`) and the client
 * confirmation prompt (`useGeoRouting.autoRouteTarget`). Returns the cluster
 * the visitor's stored preference points at — or null when auto-routing must
 * stand down entirely.
 *
 * Keyed off the stored preference alone, never the detected geo — crawlers
 * carry no cookie, so both regions stay directly crawlable and no IP-based
 * redirect can occur.
 */
export const resolveGeoPreferenceTarget = (input: GeoPreferenceTargetInput): GeoPreferenceTarget | null => {
  if (!isGeoPromptEligible(input)) {
    return null;
  }
  if (!input.preference || !input.currentGeo || input.preference === input.currentGeo) {
    return null;
  }

  const target = input.clusters.find(cluster => cluster.geo === input.preference);
  if (!target) {
    return null;
  }

  return { cluster: target, mirrored: isGeoMirroredPath(input.path) };
};

/**
 * The pure decision behind the SSR auto-redirect (the side-effectful
 * `navigateTo` lives in `middleware/09.geo-preference-routing.global.ts`).
 * Returns the absolute destination URL to redirect to, or null to stay put.
 * Only mirrored paths (present in every cluster) redirect; unmirrored paths
 * are the confirmation-dialog flow instead.
 */
export const resolveGeoPreferenceRedirect = (input: GeoPreferenceRedirectInput): string | null => {
  const target = resolveGeoPreferenceTarget(input);
  if (!target?.mirrored) {
    return null;
  }

  return buildRegionSwitchUrl(target.cluster.host, target.cluster.pathPrefix, {
    scheme: input.scheme,
    path: input.path,
    query: omitNonTransferableQueryParams(input.query),
  });
};
